import * as THREE from 'three';

const STATE_COLORS = {
  idle: new THREE.Vector3(0.3, 0.7, 1.0),
  listening: new THREE.Vector3(0.6, 0.3, 0.9),
  thinking: new THREE.Vector3(1.0, 0.6, 0.2),
  speaking: new THREE.Vector3(0.1, 0.8, 0.5),
};

const STATE_ROTATION = {
  idle: 0.001,
  listening: 0.002,
  thinking: 0.008,
  speaking: 0.003,
};

const MAIN_COUNT = 2000;
const HALO_COUNT = 500;
const DUST_COUNT = 400;

function generateSpherePoints(count, radius) {
  const positions = new Float32Array(count * 3);
  const goldenRatio = (1 + Math.sqrt(5)) / 2;

  for (let i = 0; i < count; i++) {
    const theta = (2 * Math.PI * i) / goldenRatio;
    const phi = Math.acos(1 - (2 * (i + 0.5)) / count);

    positions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
    positions[i * 3 + 2] = radius * Math.cos(phi);
  }

  return positions;
}

function generateDustParticles() {
  const positions = new Float32Array(DUST_COUNT * 3);
  for (let i = 0; i < DUST_COUNT; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 20;
    positions[i * 3 + 1] = (Math.random() - 0.5) * 14;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 10 - 2;
  }
  return positions;
}

export default class ParticleBall {
  constructor() {
    this.state = 'idle';
    this.audioAnalyser = null;
    this.frameCount = 0;
    this.currentColor = new THREE.Vector3(0.3, 0.7, 1.0);
    this.audioData = new Uint8Array(128);
    this.animationId = null;
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.mainPoints = null;
    this.haloPoints = null;
    this.dustPoints = null;
    this.basePositions = null;
    this.dustBasePositions = null;
  }

  init(container) {
    const width = container.clientWidth || 64;
    const height = container.clientHeight || 64;

    // Scene
    this.scene = new THREE.Scene();

    // Camera
    this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 100);
    this.camera.position.set(0, 0, 3.5);

    // Renderer
    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x000000, 0);
    container.appendChild(this.renderer.domElement);

    // Main particles (2000 on sphere radius 1.0)
    const mainPositions = generateSpherePoints(MAIN_COUNT, 1.0);
    this.basePositions = new Float32Array(mainPositions);
    const mainGeom = new THREE.BufferGeometry();
    mainGeom.setAttribute('position', new THREE.BufferAttribute(mainPositions, 3));
    const mainMat = new THREE.PointsMaterial({
      size: 0.012,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });
    this.mainPoints = new THREE.Points(mainGeom, mainMat);
    this.scene.add(this.mainPoints);

    // Halo particles (500 on sphere radius 1.8)
    const haloPositions = generateSpherePoints(HALO_COUNT, 1.8);
    const haloGeom = new THREE.BufferGeometry();
    haloGeom.setAttribute('position', new THREE.BufferAttribute(haloPositions, 3));
    const haloMat = new THREE.PointsMaterial({
      size: 0.008,
      transparent: true,
      opacity: 0.2,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });
    this.haloPoints = new THREE.Points(haloGeom, haloMat);
    this.scene.add(this.haloPoints);

    // Dust particles (400 ambient)
    const dustPositions = generateDustParticles();
    this.dustBasePositions = new Float32Array(dustPositions);
    const dustGeom = new THREE.BufferGeometry();
    dustGeom.setAttribute('position', new THREE.BufferAttribute(dustPositions, 3));
    const dustMat = new THREE.PointsMaterial({
      size: 0.015,
      transparent: true,
      opacity: 0.12,
      color: new THREE.Color(0.4, 0.85, 0.6),
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });
    this.dustPoints = new THREE.Points(dustGeom, dustMat);
    this.scene.add(this.dustPoints);

    // Start animation loop
    this._animate = this._animate.bind(this);
    this._animate();
  }

  _animate() {
    this.animationId = requestAnimationFrame(this._animate);
    this.frameCount++;
    const t = this.frameCount;
    const state = this.state;

    // --- Color lerp ---
    const targetColor = STATE_COLORS[state] || STATE_COLORS.idle;
    this.currentColor.lerp(targetColor, 0.03);

    // --- Audio amplitude ---
    let amplitude = 0;
    if (state === 'speaking' && this.audioAnalyser) {
      this.audioAnalyser.getByteFrequencyData(this.audioData);
      let sum = 0;
      for (let i = 0; i < this.audioData.length; i++) sum += this.audioData[i];
      amplitude = sum / this.audioData.length / 255;
    }

    // --- Main particles ---
    if (this.mainPoints) {
      const posAttr = this.mainPoints.geometry.attributes.position;
      const arr = posAttr.array;
      const rotSpeed = STATE_ROTATION[state] || STATE_ROTATION.idle;

      this.mainPoints.rotation.y += rotSpeed;
      this.mainPoints.rotation.x += rotSpeed * 0.3;

      for (let i = 0; i < MAIN_COUNT; i++) {
        const bx = this.basePositions[i * 3];
        const by = this.basePositions[i * 3 + 1];
        const bz = this.basePositions[i * 3 + 2];

        let scale = 1.0;

        if (state === 'idle') {
          scale = 1.0 + Math.sin(t * 0.02 + i * 0.1) * 0.02;
        } else if (state === 'listening') {
          scale = 1.05 + Math.sin(t * 0.03 + i * 0.05) * 0.02;
        } else if (state === 'thinking') {
          const angle = t * 0.01 + i * 0.003;
          const offset = Math.sin(angle) * 0.08;
          arr[i * 3] = bx + offset;
          arr[i * 3 + 1] = by + Math.cos(angle * 1.3) * 0.08;
          arr[i * 3 + 2] = bz + Math.sin(angle * 0.7) * 0.08;
          continue;
        } else if (state === 'speaking') {
          scale = 1.0 + amplitude * 0.4;
        }

        arr[i * 3] = bx * scale;
        arr[i * 3 + 1] = by * scale;
        arr[i * 3 + 2] = bz * scale;
      }

      posAttr.needsUpdate = true;

      this.mainPoints.material.color.setRGB(
        this.currentColor.x,
        this.currentColor.y,
        this.currentColor.z
      );
    }

    // --- Halo ---
    if (this.haloPoints) {
      this.haloPoints.rotation.y -= (STATE_ROTATION[state] || STATE_ROTATION.idle) * 0.5;
      this.haloPoints.material.color.setRGB(
        this.currentColor.x,
        this.currentColor.y,
        this.currentColor.z
      );
    }

    // --- Dust ---
    if (this.dustPoints) {
      const dustAttr = this.dustPoints.geometry.attributes.position;
      const dustArr = dustAttr.array;

      for (let i = 0; i < DUST_COUNT; i++) {
        const idx = i * 3;
        dustArr[idx] = this.dustBasePositions[idx] + Math.sin(t * 0.003 + i * 0.7) * 0.4;
        dustArr[idx + 1] = this.dustBasePositions[idx + 1] + Math.sin(t * 0.002 + i * 1.1) * 0.5;
        dustArr[idx + 2] = this.dustBasePositions[idx + 2] + Math.cos(t * 0.0025 + i * 0.5) * 0.3;
      }

      dustAttr.needsUpdate = true;
      this.dustPoints.rotation.y += 0.00015;
    }

    // --- Render ---
    this.renderer.render(this.scene, this.camera);
  }

  setState(state) {
    this.state = state;
  }

  setAudioAnalyser(analyser) {
    this.audioAnalyser = analyser;
  }

  resize(w, h) {
    if (!this.renderer || !this.camera) return;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  getCanvas() {
    return this.renderer ? this.renderer.domElement : null;
  }

  destroy() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    if (this.renderer) {
      this.renderer.dispose();
      const canvas = this.renderer.domElement;
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      this.renderer = null;
    }
    if (this.mainPoints) {
      this.mainPoints.geometry.dispose();
      this.mainPoints.material.dispose();
    }
    if (this.haloPoints) {
      this.haloPoints.geometry.dispose();
      this.haloPoints.material.dispose();
    }
    if (this.dustPoints) {
      this.dustPoints.geometry.dispose();
      this.dustPoints.material.dispose();
    }
    this.scene = null;
    this.camera = null;
  }
}
